import React, { useState } from 'react';
import { 
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface CloseMissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  missionNumber: string;
  isReopening?: boolean;
}

const CloseMissionModal: React.FC<CloseMissionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  missionNumber,
  isReopening = false
}) => {
  const [notes, setNotes] = useState('');
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (confirmText.toUpperCase() === 'CONFIRM') {
      onConfirm(notes);
      // Reset state
      setNotes('');
      setConfirmText('');
    }
  };

  const Icon = isReopening ? ArrowPathIcon : CheckCircleIcon;
  const iconBgColor = isReopening ? 'bg-yellow-100' : 'bg-red-100';
  const iconColor = isReopening ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isReopening ? 'Reopen Mission' : 'Close Mission'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className={`flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} ${iconColor}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {isReopening 
                  ? `Are you sure you want to reopen mission ${missionNumber}?`
                  : `Are you sure you want to close mission ${missionNumber}?`
                }
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {isReopening
                  ? 'This will move the mission back to active status.'
                  : 'This action will mark the mission as complete and archive it.'
                }
              </p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder={isReopening 
                ? "Reason for reopening mission..." 
                : "Final notes or summary..."
              }
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-bold">CONFIRM</span> to {isReopening ? 'reopen' : 'close'} this mission
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Type CONFIRM"
            />
          </div>

          {!isReopening && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-800 ml-2">
                  Ensure all required documentation and reports have been completed before closing.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmText.toUpperCase() !== 'CONFIRM'}
            className={`btn-primary ${
              confirmText.toUpperCase() !== 'CONFIRM' 
                ? 'opacity-50 cursor-not-allowed' 
                : ''
            }`}
          >
            {isReopening ? 'Reopen Mission' : 'Close Mission'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloseMissionModal;